import { useCallback, useEffect, useRef, useState } from 'react'

import useSWR from 'swr'

import { streamAnalyzeKeywords } from '@/common/api/articleCreatorStream'
import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiDeleteFetcher, apiPostFetcher } from '@/common/api/fetcher'
import {
  AnalyzeContentGapResponse,
  AnalyzeContentGapVars,
  AnalyzeKeywordsStreamResult,
  AnalyzeKeywordsStreamVars,
  AnalyzeLinkStructureResponse,
  AnalyzeLinkStructureVars,
  ArticleApiPath,
  CompareContentGapResponse,
  CompareContentGapVars,
  CompareExtGapResponse,
  DetectCannibalizationResponse,
  DetectCannibalizationVars,
  GenerateArticleResponse,
  GenerateArticleVars,
  GenerateOutlineResponse,
  GenerateOutlineVars,
  GetAnalysisDetailResponse,
  GetAnalysisDetailVars,
  GetAnalysisHistoryResponse,
  GetAnalysisHistoryVars,
  KeywordAnalysisProgressEvent,
  RunAnalysisResponse,
  RunAnalysisVars,
  TimelineExtGapResponse
} from '@/common/api/params/article'
import { ExtGapType } from '@/common/model/media/articleCreator'

// -----------------------------------------------
// GET hooks
// -----------------------------------------------

export const useAnalysisHistoriesApi = (vars: GetAnalysisHistoryVars | null) => {
  return useSWR<GetAnalysisHistoryResponse, Response, [string, GetAnalysisHistoryVars] | null>(
    vars ? [ArticleApiPath.GET_ANALYSIS_HISTORY, vars] : null,
    apiDefaultFetcher
  )
}

// -----------------------------------------------
// Mutation hooks（GETの分析実行もボタン起点なので mutation として扱う）
// -----------------------------------------------

export const useAnalysisDetailMutation = (options?: MutationOptions<GetAnalysisDetailResponse>) => {
  return useCallMutation<GetAnalysisDetailResponse, GetAnalysisDetailVars>(
    ArticleApiPath.GET_ANALYSIS_DETAIL,
    options,
    apiDefaultFetcher
  )
}

export const useDeleteAnalysisHistoryMutation = (
  options?: MutationOptions<Record<string, never>>
) => {
  return useCallMutation<Record<string, never>, GetAnalysisDetailVars>(
    ArticleApiPath.GET_ANALYSIS_DETAIL,
    options,
    apiDeleteFetcher
  )
}

export const useGenerateArticleMutation = (options?: MutationOptions<GenerateArticleResponse>) => {
  return useCallMutation<GenerateArticleResponse, GenerateArticleVars>(
    ArticleApiPath.GENERATE_ARTICLE,
    options
  )
}

export const useGenerateOutlineMutation = (options?: MutationOptions<GenerateOutlineResponse>) => {
  return useCallMutation<GenerateOutlineResponse, GenerateOutlineVars>(
    ArticleApiPath.GENERATE_OUTLINE,
    options
  )
}

export const useAnalyzeContentGapMutation = (
  options?: MutationOptions<AnalyzeContentGapResponse>
) => {
  return useCallMutation<AnalyzeContentGapResponse, AnalyzeContentGapVars>(
    ArticleApiPath.ANALYZE_CONTENT_GAP,
    options,
    apiDefaultFetcher
  )
}

export const useCompareContentGapMutation = (
  options?: MutationOptions<CompareContentGapResponse>
) => {
  return useCallMutation<CompareContentGapResponse, CompareContentGapVars>(
    ArticleApiPath.COMPARE_CONTENT_GAP,
    options,
    apiDefaultFetcher
  )
}

export const useDetectCannibalizationMutation = (
  options?: MutationOptions<DetectCannibalizationResponse>
) => {
  return useCallMutation<DetectCannibalizationResponse, DetectCannibalizationVars>(
    ArticleApiPath.DETECT_CANNIBALIZATION,
    options,
    apiDefaultFetcher
  )
}

export const useAnalyzeLinkStructureMutation = (
  options?: MutationOptions<AnalyzeLinkStructureResponse>
) => {
  return useCallMutation<AnalyzeLinkStructureResponse, AnalyzeLinkStructureVars>(
    ArticleApiPath.ANALYZE_LINK_STRUCTURE,
    options,
    apiDefaultFetcher
  )
}

// -----------------------------------------------
// バックグラウンド実行（run）
// レコードを analyzing で作成し、即座に id/status を返す。
// 呼び出し側は返却された id の詳細画面へ即遷移し、そこで status をポーリングする。
// -----------------------------------------------

export const useRunContentGapMutation = (options?: MutationOptions<RunAnalysisResponse>) => {
  return useCallMutation<RunAnalysisResponse, RunAnalysisVars>(
    ArticleApiPath.RUN_CONTENT_GAP,
    options,
    apiPostFetcher
  )
}

export const useRunCannibalizationMutation = (options?: MutationOptions<RunAnalysisResponse>) => {
  return useCallMutation<RunAnalysisResponse, RunAnalysisVars>(
    ArticleApiPath.RUN_CANNIBALIZATION,
    options,
    apiPostFetcher
  )
}

export const useRunLinkStructureMutation = (options?: MutationOptions<RunAnalysisResponse>) => {
  return useCallMutation<RunAnalysisResponse, RunAnalysisVars>(
    ArticleApiPath.RUN_LINK_STRUCTURE,
    options,
    apiPostFetcher
  )
}

// 拡張分析（カニバリ/内部リンク）の比較・時系列はタイプ別にパスが異なる
const extComparePath = (type: ExtGapType): string => {
  switch (type) {
    case ExtGapType.CANNIBALIZATION:
      return ArticleApiPath.COMPARE_CANNIBALIZATION
    case ExtGapType.LINK_STRUCTURE:
    default:
      return ArticleApiPath.COMPARE_LINK_STRUCTURE
  }
}

const extTimelinePath = (type: ExtGapType): string => {
  switch (type) {
    case ExtGapType.CANNIBALIZATION:
      return ArticleApiPath.TIMELINE_CANNIBALIZATION
    case ExtGapType.LINK_STRUCTURE:
    default:
      return ArticleApiPath.TIMELINE_LINK_STRUCTURE
  }
}

export const useCompareExtGapMutation = (
  type: ExtGapType,
  options?: MutationOptions<CompareExtGapResponse>
) => {
  return useCallMutation<CompareExtGapResponse, { site_id: number }>(
    extComparePath(type),
    options,
    apiDefaultFetcher
  )
}

export const useTimelineExtGapMutation = (
  type: ExtGapType,
  options?: MutationOptions<TimelineExtGapResponse>
) => {
  return useCallMutation<TimelineExtGapResponse, { site_id: number }>(
    extTimelinePath(type),
    options,
    apiDefaultFetcher
  )
}

// -----------------------------------------------
// SSE streaming hook（キーワード分析）
// -----------------------------------------------

export interface UseAnalyzeKeywordsStreamState {
  analyzing: boolean
  progress: KeywordAnalysisProgressEvent | null
  result: AnalyzeKeywordsStreamResult | null
  error: string | null
  start: (vars: AnalyzeKeywordsStreamVars) => Promise<void>
  setResult: (result: AnalyzeKeywordsStreamResult | null) => void
  reset: () => void
}

export const useAnalyzeKeywordsStream = (): UseAnalyzeKeywordsStreamState => {
  const [analyzing, setAnalyzing] = useState(false)
  const [progress, setProgress] = useState<KeywordAnalysisProgressEvent | null>(null)
  const [result, setResult] = useState<AnalyzeKeywordsStreamResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const reset = useCallback(() => {
    setAnalyzing(false)
    setProgress(null)
    setResult(null)
    setError(null)
  }, [])

  const start = useCallback(async (vars: AnalyzeKeywordsStreamVars) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setAnalyzing(true)
    setResult(null)
    setError(null)
    setProgress({
      step: 0,
      total_steps: 6,
      percent: 0,
      message: '分析を開始しています...',
      detail: ''
    })

    try {
      await streamAnalyzeKeywords(
        vars,
        {
          onProgress: (p) => mountedRef.current && setProgress(p),
          onResult: (r) => mountedRef.current && setResult(r)
        },
        controller.signal
      )
    } catch (e) {
      if (mountedRef.current && !(e instanceof DOMException && e.name === 'AbortError')) {
        setError('分析に失敗しました')
      }
    } finally {
      if (mountedRef.current) {
        setAnalyzing(false)
        setProgress(null)
      }
    }
  }, [])

  return { analyzing, progress, result, error, start, setResult, reset }
}
